import { useEffect, useState } from "react";
import {
  api,
  type ListingComps,
  type TcgProduct,
  type PriceChartingProduct,
  type ListingPsa,
} from "../lib/api";
import { fmtCents } from "../lib/format";

type Field = "resale_floor" | "sold_floor" | "sold_median" | "bin_floor";

type Props = {
  listingId: number;
  field: Field;
  onClose: () => void;
};

const FIELD_LABEL: Record<Field, string> = {
  resale_floor: "Resale floor",
  sold_floor: "Sold floor",
  sold_median: "Sold median",
  bin_floor: "BIN floor",
};

export default function CompsDialog({ listingId, field, onClose }: Props) {
  const [data, setData] = useState<ListingComps | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tcg, setTcg] = useState<TcgProduct[] | "loading" | "missing">("loading");
  const [pc, setPc] = useState<PriceChartingProduct | null | "loading" | "missing">(
    "loading",
  );
  const [psa, setPsa] = useState<ListingPsa | "loading" | "missing">("loading");

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setErr(null);
    setTcg("loading");
    api
      .getListingComps(listingId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setErr(String(e));
      });
    api
      .getListingTcgPlayer(listingId)
      .then((res) => {
        if (cancelled) return;
        setTcg(res.products && res.products.length > 0 ? res.products : "missing");
      })
      .catch(() => {
        if (!cancelled) setTcg("missing");
      });
    setPc("loading");
    api
      .getListingPriceCharting(listingId)
      .then((res) => {
        if (cancelled) return;
        setPc(res.product ?? "missing");
      })
      .catch(() => {
        if (!cancelled) setPc("missing");
      });
    setPsa("loading");
    api
      .getListingPsa(listingId)
      .then((res) => {
        if (cancelled) return;
        setPsa(res);
      })
      .catch(() => {
        if (!cancelled) setPsa("missing");
      });
    return () => {
      cancelled = true;
    };
  }, [listingId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Pick which set of comps drove this field. Sold-derived stats use sold
  // comps; bin-derived stats use bin comps. Resale floor mixes both — show
  // both lists with sold first.
  const showSold = field !== "bin_floor";
  const showBin = field === "bin_floor" || field === "resale_floor";

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/70 p-8 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded shadow-xl w-full max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <h2 className="font-mono text-sm text-zinc-200">
            {FIELD_LABEL[field]} — comps used
          </h2>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-200 text-lg leading-none"
            aria-label="close"
          >
            ×
          </button>
        </div>
        <div className="p-3 max-h-[70vh] overflow-y-auto">
          {!data && !err && (
            <div className="text-zinc-500 text-sm font-mono">loading…</div>
          )}
          {err && (
            <div className="text-red-400 text-sm font-mono">{err}</div>
          )}
          {data && showSold && (
            <CompTable
              title={`Sold (${data.sold.length})`}
              rows={data.sold.map((s) => ({
                date: s.sold_date,
                title: s.title,
                price: s.total_cents,
                link: s.link,
              }))}
              dateLabel="sold"
              empty="No sold comps stored for this listing yet."
            />
          )}
          {data && showBin && (
            <div className={showSold ? "mt-4" : ""}>
              <CompTable
                title={`Active BIN (${data.bin.length})`}
                rows={data.bin.map((b) => ({
                  date: null,
                  title: b.title,
                  price: b.total_cents,
                  link: b.link,
                }))}
                dateLabel=""
                empty="No active BIN comps stored for this listing."
              />
            </div>
          )}
          <div className="mt-4 pt-3 border-t border-zinc-800">
            <PriceChartingPanel state={pc} />
          </div>
          <div className="mt-4 pt-3 border-t border-zinc-800">
            <PsaPanel state={psa} />
          </div>
          <div className="mt-4 pt-3 border-t border-zinc-800">
            <TcgPlayerPanel state={tcg} />
          </div>
        </div>
      </div>
    </div>
  );
}

type Row = { date: string | null; title: string; price: number; link: string };

function CompTable({
  title,
  rows,
  dateLabel,
  empty,
}: {
  title: string;
  rows: Row[];
  dateLabel: string;
  empty: string;
}) {
  if (rows.length === 0) {
    return (
      <div>
        <div className="text-zinc-400 font-mono text-xs mb-1">{title}</div>
        <div className="text-zinc-600 text-xs font-mono">{empty}</div>
      </div>
    );
  }
  const sorted = [...rows].sort((a, b) => {
    if (a.date && b.date) return a.date < b.date ? 1 : -1;
    return a.price - b.price;
  });
  return (
    <div>
      <div className="text-zinc-400 font-mono text-xs mb-1">{title}</div>
      <table className="w-full text-xs font-mono">
        <thead className="text-zinc-600">
          <tr>
            {dateLabel && <th className="text-left px-2 py-1">{dateLabel}</th>}
            <th className="text-left px-2 py-1">title</th>
            <th className="text-right px-2 py-1">price</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr
              key={i}
              className="border-t border-zinc-900 hover:bg-zinc-900/60 cursor-pointer"
              onClick={() => window.open(r.link, "_blank", "noopener,noreferrer")}
            >
              {dateLabel && (
                <td className="px-2 py-1 text-zinc-400 whitespace-nowrap">
                  {r.date ? r.date.slice(0, 10) : "—"}
                </td>
              )}
              <td
                className="px-2 py-1 text-zinc-200 truncate max-w-2xl"
                title={r.title}
              >
                {r.title}
              </td>
              <td className="px-2 py-1 text-right text-zinc-200">
                {fmtCents(r.price)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PsaPanel({ state }: { state: ListingPsa | "loading" | "missing" }) {
  if (state === "loading") {
    return (
      <div className="text-zinc-500 text-xs font-mono">
        PSA pop / auctions — loading…
      </div>
    );
  }
  if (state === "missing") {
    return (
      <div className="text-zinc-600 text-xs font-mono">
        PSA pop / auctions — unavailable
      </div>
    );
  }
  const { spec, sales, cert } = state;
  if (!spec && sales.length === 0 && !cert) {
    return (
      <div className="text-zinc-600 text-xs font-mono">
        PSA pop / auctions — no match
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="text-zinc-400 font-mono text-xs">PSA</div>
      {spec && (
        <a
          href={spec.url}
          target="_blank"
          rel="noreferrer"
          className="block bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-800 rounded p-2 font-mono text-xs"
        >
          <div className="flex items-center gap-3 mb-2">
            <span className="text-zinc-200">{spec.card_name}</span>
            {spec.set_name && <span className="text-zinc-500 truncate">{spec.set_name}</span>}
            {spec.year && <span className="text-zinc-600">{spec.year}</span>}
          </div>
          <div className="grid grid-cols-6 gap-2 text-center">
            {[
              ["pop", spec.pop_total],
              ["PSA 10", spec.pop_psa10],
              ["PSA 9.5", spec.pop_psa9_5],
              ["PSA 9", spec.pop_psa9],
              ["PSA 8", spec.pop_psa8],
              ["PSA 7", spec.pop_psa7],
            ].map(([label, n]) => (
              <div key={String(label)}>
                <div className="text-zinc-600 text-[10px]">{label}</div>
                <div
                  className={`${
                    label === "PSA 10" ? "text-yellow-300 font-bold" : "text-zinc-300"
                  }`}
                >
                  {n != null ? Number(n).toLocaleString() : "—"}
                </div>
              </div>
            ))}
          </div>
        </a>
      )}
      {sales.length > 0 && (
        <div>
          <div className="text-zinc-500 font-mono text-[10px] mb-1">
            Recent PSA-graded auction sales
          </div>
          <table className="w-full text-xs font-mono">
            <thead className="text-zinc-600">
              <tr>
                <th className="text-left px-2 py-1">date</th>
                <th className="text-left px-2 py-1">grade</th>
                <th className="text-right px-2 py-1">price</th>
                <th className="text-left px-2 py-1">house</th>
              </tr>
            </thead>
            <tbody>
              {sales.slice(0, 8).map((s, i) => (
                <tr
                  key={i}
                  className="border-t border-zinc-900 hover:bg-zinc-900/60 cursor-pointer"
                  onClick={() =>
                    s.lot_url && window.open(s.lot_url, "_blank", "noopener,noreferrer")
                  }
                >
                  <td className="px-2 py-1 text-zinc-400 whitespace-nowrap">
                    {s.sold_at ?? "—"}
                  </td>
                  <td className="px-2 py-1 text-zinc-300">{s.grade ?? "—"}</td>
                  <td className="px-2 py-1 text-right text-zinc-200">
                    {fmtCents(s.price_cents)}
                  </td>
                  <td className="px-2 py-1 text-zinc-500 truncate max-w-[160px]">
                    {s.source ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {cert && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2 font-mono text-xs">
          <div className="text-zinc-500 text-[10px] mb-1">Cert verification</div>
          <div className="flex items-center gap-3">
            <span
              className={
                cert.status === "ok"
                  ? "text-green-400"
                  : cert.status === "not_found"
                    ? "text-red-400"
                    : "text-zinc-500"
              }
            >
              #{cert.cert_no} —{" "}
              {cert.status === "ok"
                ? "verified"
                : cert.status === "not_found"
                  ? "no PSA record"
                  : cert.status === "challenge"
                    ? "couldn't verify (PSA challenge)"
                    : "error"}
            </span>
            {cert.grade && <span className="text-zinc-300">{cert.grade}</span>}
            {cert.card_name && <span className="text-zinc-500 truncate">{cert.card_name}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function PriceChartingPanel({
  state,
}: {
  state: PriceChartingProduct | null | "loading" | "missing";
}) {
  if (state === "loading") {
    return (
      <div className="text-zinc-500 text-xs font-mono">
        PriceCharting graded reference — loading…
      </div>
    );
  }
  if (state === "missing" || state === null) {
    return (
      <div className="text-zinc-600 text-xs font-mono">
        PriceCharting graded reference — no match (or no API token)
      </div>
    );
  }
  // Each price is the published reference for that grade tier.
  // Empty/zero values are rendered as "—" (PriceCharting only fills
  // tiers where it has enough sales data).
  const tiers: Array<[string, number | null]> = [
    ["raw", state.loose_price_cents],
    ["PSA 7", state.cib_price_cents],
    ["PSA 8", state.new_price_cents],
    ["PSA 9", state.graded_price_cents],
    ["PSA 9.5", state.box_only_price_cents],
    ["PSA 10", state.manual_only_price_cents],
    ["BGS 10", state.bgs_10_price_cents],
    ["CGC 10", state.condition_17_price_cents],
    ["SGC 10", state.condition_18_price_cents],
  ];
  return (
    <div>
      <div className="text-zinc-400 font-mono text-xs mb-1">
        PriceCharting graded reference
      </div>
      <a
        href={`https://www.pricecharting.com/game/${state.product_id}`}
        target="_blank"
        rel="noreferrer"
        className="block bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-800 rounded p-2 font-mono text-xs"
      >
        <div className="flex items-center gap-3 mb-2">
          <span className="text-zinc-200">{state.product_name}</span>
          {state.console_name && (
            <span className="text-zinc-500">{state.console_name}</span>
          )}
          {state.release_date && (
            <span className="text-zinc-600">{state.release_date.slice(0, 4)}</span>
          )}
        </div>
        <div className="grid grid-cols-9 gap-1 text-center">
          {tiers.map(([label, cents]) => (
            <div key={label}>
              <div className="text-zinc-600 text-[10px]">{label}</div>
              <div
                className={`${
                  label === "PSA 10" ? "text-yellow-300 font-bold" : "text-zinc-300"
                }`}
              >
                {cents !== null && cents > 0 ? fmtCents(cents) : "—"}
              </div>
            </div>
          ))}
        </div>
      </a>
    </div>
  );
}

function TcgPlayerPanel({
  state,
}: {
  state: TcgProduct[] | "loading" | "missing";
}) {
  if (state === "loading") {
    return (
      <div className="text-zinc-500 text-xs font-mono">
        TCGplayer raw market — loading…
      </div>
    );
  }
  if (state === "missing") {
    return (
      <div className="text-zinc-600 text-xs font-mono">
        TCGplayer raw market — no match
      </div>
    );
  }
  // Multiple matches because card names often map to several
  // TCGplayer SKUs (regular printing vs Shiny Vault vs Promo, etc.).
  // Letting the user eyeball is more honest than auto-picking the
  // wrong variant. Raw market is the ungraded floor — PSA 10 should
  // price well above whichever printing is the right match.
  // Single-result mode means we resolved the card identity at scan
  // time so the API picked the right variant. Multi-result mode is
  // the unresolved fallback — show all candidates and let the user
  // pick the right printing visually.
  const isResolved = state.length === 1;
  return (
    <div>
      <div className="text-zinc-400 font-mono text-xs mb-1">
        {isResolved
          ? "TCGplayer raw market"
          : `TCGplayer raw market — top ${state.length} candidates`}
      </div>
      <div className="space-y-1">
        {state.map((p) => (
          <a
            key={p.product_id}
            href={p.url}
            target="_blank"
            rel="noreferrer"
            className="block bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-800 rounded p-2 font-mono text-xs"
          >
            <div className="flex items-center gap-3 mb-1">
              <span className="text-zinc-200">{p.name}</span>
              {p.set_name && <span className="text-zinc-500">{p.set_name}</span>}
              {p.card_number && (
                <span className="text-zinc-500">#{p.card_number}</span>
              )}
              {p.rarity && <span className="text-zinc-500">{p.rarity}</span>}
            </div>
            <div className="grid grid-cols-4 gap-2 text-zinc-300">
              <div>
                <div className="text-zinc-600">market</div>
                <div>{fmtCents(p.market_price_cents)}</div>
              </div>
              <div>
                <div className="text-zinc-600">low</div>
                <div>{fmtCents(p.low_listing_cents)}</div>
              </div>
              <div>
                <div className="text-zinc-600">recent</div>
                <div>{fmtCents(p.recent_sale_cents)}</div>
              </div>
              <div>
                <div className="text-zinc-600">listings</div>
                <div>{p.listings_count ?? "—"}</div>
              </div>
            </div>
          </a>
        ))}
      </div>
      <div className="text-zinc-600 mt-1 text-[10px]">
        Raw card baseline (ungraded). PSA 10 should price well above whichever printing matches.
      </div>
    </div>
  );
}
